import { Directive, inject, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, FormControl, UntypedFormGroup } from '@angular/forms';
import { distinctUntilChanged, Subscription } from 'rxjs';
import { unsubscribeAll } from 'src/app/common/app.helpers';
import { ValueTypes } from 'src/app/common/custom.types';
import { FormStateService } from 'src/app/forms/form-state.service';
import { FormValidationService } from 'src/app/forms/form-validation.service';
import { Field, FieldAttribute } from 'src/app/interfaces/form-and-field.interface';
import { MaybeNull } from 'src/app/utils';
import {
  getDiseaseTooltipMessage,
  getNewPlaceholderForMobile,
  getOrganizationProjectTooltipTitle,
} from '../../forms.helpers';

@Directive()
export class FormFieldSate implements Field, OnInit, OnDestroy {
  public fieldAttribute: FieldAttribute;
  public form: UntypedFormGroup;
  public newPlaceholder: string;
  public tooltipDescription = '';
  public isShowInfoTooltip: boolean;
  public organizationsProjectTooltipText = '';

  protected control: FormControl;
  protected subscriptions: Subscription[] = [];

  protected formValidationService = inject(FormValidationService);
  protected formStateService = inject(FormStateService);

  public ngOnInit(): void {
    this.initField();
    this.subscribeToControlValueChanges();

    this.newPlaceholder = getNewPlaceholderForMobile(
      this.fieldAttribute.id,
      this.fieldAttribute.backendAttribute.placeholder,
    );
    this.tooltipDescription = getDiseaseTooltipMessage(this.fieldAttribute.id);
    this.isShowInfoTooltip =
      this.fieldAttribute.backendAttribute.placeholder !== this.newPlaceholder || Boolean(this.tooltipDescription);
    this.organizationsProjectTooltipText = getOrganizationProjectTooltipTitle(this.fieldAttribute.id);
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions);
  }

  public isFieldInvalid(): boolean {
    return this.formValidationService.isInvalid(this.control);
  }

  public getValidationError(formControl: MaybeNull<AbstractControl>): string {
    if (formControl) {
      return this.formValidationService.getValidationError(formControl);
    }

    return '';
  }

  public isControlDisabled(): boolean {
    return this.control.disabled;
  }

  public hasControlValue(): boolean {
    return Boolean(this.control.value);
  }

  protected initField(): void {
    this.control = this.formStateService.initControl(this.form, this.fieldAttribute);
  }

  protected onNullControlValue(): void {
    //Simply ignore
  }

  protected onDefinedControlValue(_value: ValueTypes): void {
    //Simply ignore
  }

  protected subscribeToControlValueChanges(): void {
    this.setupControlValueChangesSubscription(true);
  }

  protected setupControlValueChangesSubscription(useDistinctUntilChanged: boolean): void {
    const observer = useDistinctUntilChanged
      ? this.control.valueChanges.pipe(distinctUntilChanged())
      : this.control.valueChanges;

    this.subscriptions.push(
      observer.subscribe((value: ValueTypes) => {
        if (value === null) {
          this.onNullControlValue();
        } else {
          this.onDefinedControlValue(value);
        }
      }),
    );
  }
}
